// [목적] 모바일 /explore — 검색·필터칩 헤더 + 공식 컬렉션 가로 스크롤 + 폴리오 2열 그리드
// [이유] W2(design-alignment): 쿨톤 → paper/ink/line 정본 토큰 교체.
//         W4(design-alignment): 필터칩 10종, ⌕ 검색 아이콘, days 배지 좌상단.
//         fe-be-gap-tier1 T10(ADR-T1-3·ADR-TR-5): OFFICIAL mock 제거 →
//           collection.client.list({ kind:'official', limit:6 }) 실데이터.
//           카드 클릭 → /collections/:id(ADR-TR-7). 0건이면 섹션 graceful(숨김).
//         fe-mobile-live-data: EXPLORE_FOLIOS(design-mock) 제거 → useFolioFeed(ExplorePage.tsx 데스크톱과
//           동일 훅) 실데이터 배선. 카드 좋아요는 FolioLikeChip(useFolioLike) 실배선.
//         M7 P1(발견 화면 서버 검색·필터): 검색어(300ms 디바운스) + 필터 모달(기간·테마) 을
//           useExploreFilters(URL 쿼리, ExplorePage.tsx 데스크톱과 공유) 로 동기화 →
//           useFolioFeed(q/tags/minDurationDays/maxDurationDays) 서버 조회(클라이언트 제목/vibe 필터 제거).
//           필터 버튼 신규 추가(⚙) — components/explore/FilterModal.tsx 공용 컴포넌트 재사용.
// [사이드 이팩트] 컬렉션 카드 클릭 /folio/:id → /collections/:id 변경(T10). mocks/fixtures/design-mock import 제거.
//                 filterModalOpen state 추가.
// [수정 시 고려사항] pages/auth/** 쿨톤은 의도된 전용 톤(ADR-DA6) — 절대 변경 금지.
//                   필터칩 라인업 변경 시 ExplorePage.tsx FILTERS 와 동기 필수.
//                   필터칩(FILTERS 10종)도 URL 필터에서 파생·갱신(useExploreFilters CHIP_FILTERS, 2026-09-17).
//                   [G1-2, 2026-09-17] feedError 안내를 공용 InlineErrorState(재시도)로 교체.

import { Box, ButtonBase, CircularProgress, InputBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type { Collection, Folio } from '@gangwoncanvas/types';

import * as collectionClient from '../../api/collection.client.js';
import { queryKeys } from '../../api/queryKeys.js';
import { FolioLikeChip } from '../../components/folio/FolioLikeChip.js';
import { InlineErrorState } from '../../components/feedback/InlineErrorState.js';
import { FilterModal } from '../../components/explore/FilterModal.js';
import { useFolioFeed } from '../../hooks/useFolioFeed.js';
import { useExploreFilters } from '../../hooks/useExploreFilters.js';
import { folioAuthorLabel } from '../../utils/folioAuthorLabel.js';

// [목적] paper/ink/line 정본 토큰 바인딩 — W2(design-alignment) 쿨톤 정본화
// [이유] ADR-TR8: c.text.*(쿨톤) → c.ink.*(warm), c.surface.*(쿨톤) → c.paper.*(warm). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
//         신규 토큰 추가 없이 paper/ink/line 만 사용.
// [사이드 이팩트] 배경색 #F6F8FB → #FAF9F7, 텍스트 계열 warm 전환
// [수정 시 고려사항] ink.low(#6F6A5C)는 본문 미사용 보조 텍스트 한정(ADR-TR8). [ADR-TR8 예외는 2026-09-18 폐기 — ink.low(#6F6A5C) 는 AA 충족이라 텍스트 사용 가능]
const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L]; // #29261B (← 쿨톤 c.text.high #14181F)
const TEXT_MID = c.ink.mid[L]; // #5C5849 (← 쿨톤 c.text.mid #586069)
const TEXT_LOW = c.ink.low[L]; // #6F6A5C — 보조 텍스트 한정(ADR-TR8, 본문 미사용)
const BORDER = c.line.base[L]; // #E8E2D5 (← #E1E7EF)
const BG1 = c.paper[1][L]; // #FAF9F7 (← #F6F8FB)
const BRAND = c.brand[500][L];
// [2026-09-18] 활성 필터칩·"전체 →" 소형 브랜드 텍스트 전용 진한 브랜드 — brand[500] 는 활성 필터칩
// 텍스트가 브랜드 10% 틴트 배경 위 4.17:1, "전체 →" 텍스트가 paper[1] 위 4.4:1 로 AA 미달이었다
// (docs/qa/2026-09-17_contrast-measure.md). brand[600] 은 각각 5.84/6.36:1.
const BRAND_TEXT_STRONG = c.brand[600][L];

/**
 * [목적] 필터칩 최종 10종 — ADR-DA4 확정
 * [이유] W4: 시안 8종 + 서핑 + 무장애 병합 (중복 없음). ExplorePage 와 동일 라인업 필수.
 * [사이드 이팩트] id 는 mock 필터 식별자 — BE 태그 연결 시 교체
 * [수정 시 고려사항] 라인업 변경 시 ExplorePage.tsx FILTERS 와 동기 필수
 */
const FILTERS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'pet', label: '🐾 반려견' },
  { id: 'cafe', label: '☕ 카페' },
  { id: 'camp', label: '⛺ 캠핑' },
  { id: 'sea', label: '🌊 바다' },
  { id: 'mountain', label: '🏔 산' },
  { id: 'food', label: '🍜 맛집' },
  { id: 'short', label: '⏱ 당일' },
  { id: 'surf', label: '🏄 서핑' },
  { id: 'access', label: '♿ 무장애' },
];

// [목적] 공식 컬렉션 카드 배경 색상 순환 — T10(인덱스 기반)
// [이유] Collection 엔티티에 color 필드 없음 — 인덱스 순환으로 다양한 배경 제공.
// [수정 시 고려사항] 토큰에 컬렉션 배경 색 추가 시 교체.
const MOBILE_COLLECTION_BG_CYCLE = [
  'rgba(234,244,255,1)',
  'rgba(255,244,232,1)',
  'rgba(232,248,239,1)',
] as const;

/**
 * [목적] 발견 모바일 폴리오 카드 — 실데이터(Folio) 기반, 좋아요는 FolioLikeChip 실배선
 * [이유] fe-mobile-live-data — ExploreFolioMock 목업 카드를 실 Folio 타입으로 교체.
 * [사이드 이팩트] 없음 — FolioLikeChip 이 자체 캐시로 좋아요 상태 관리.
 * [수정 시 고려사항] coverImageUrl 없으면 이모지 폴백(🗺).
 */
function MobileExploreCard({ f, onClick }: { f: Folio; onClick: () => void }): JSX.Element {
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`폴리오 ${f.title}`}
      sx={{
        display: 'block',
        width: '100%',
        bgcolor: c.paper[0][L],
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(41,38,27,.06)',
        border: `1px solid ${BORDER}`,
        textAlign: 'left',
      }}
    >
      <Box
        sx={{
          height: 90,
          bgcolor: f.themeColor ?? c.paper[2][L],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 34,
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        {f.coverImageUrl ? (
          <Box
            component="img"
            src={f.coverImageUrl}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          '🗺'
        )}
        {f.duration.days && (
          <Box
            sx={{
              position: 'absolute',
              top: '9px',
              left: '9px',
              bgcolor: 'rgba(255,255,255,.95)',
              borderRadius: '6px',
              px: '6px',
              py: '1px',
              fontSize: 9,
              color: TEXT_HI,
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            {f.duration.days}일
          </Box>
        )}
      </Box>
      <Box sx={{ p: '8px 10px 10px' }}>
        <Typography
          sx={{ fontSize: 11, fontWeight: 600, color: TEXT_HI, lineHeight: 1.35, mb: '5px' }}
        >
          {f.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap', mb: '6px' }}>
          {f.vibe.slice(0, 3).map((t) => (
            <Box
              key={t}
              component="span"
              sx={{
                fontSize: 9,
                bgcolor: BG1,
                color: TEXT_MID,
                borderRadius: '5px',
                px: '5px',
                py: '1px',
              }}
            >
              #{t}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component="span" sx={{ fontSize: 9, color: TEXT_LOW }}>
            {folioAuthorLabel(f.author)}
          </Box>
          <FolioLikeChip folio={f} />
        </Box>
      </Box>
    </ButtonBase>
  );
}

export function ExploreMobile(): JSX.Element {
  const navigate = useNavigate();
  // 카테고리 칩 10종 — 서버 미연결, 시각적 토글만(FilterModal.tsx 헤더 주석 참조)
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const exploreFilters = useExploreFilters();
  // [2026-09-17] 카테고리 칩은 URL 필터(tags/duration)에서 파생 — 서버 조회와 동일 상태(useExploreFilters CHIP_FILTERS)
  const filter = exploreFilters.chip;
  const setFilter = exploreFilters.setChip;

  // M7 P1: 검색 입력 로컬 버퍼 — 300ms 디바운스 후 URL 쿼리 반영(ExplorePage.tsx 데스크톱과 동일 패턴)
  const [qInput, setQInput] = useState(exploreFilters.q);
  useEffect(() => setQInput(exploreFilters.q), [exploreFilters.q]);
  useEffect(() => {
    const t = setTimeout(() => exploreFilters.setQ(qInput), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  // [목적] 공식 컬렉션 실데이터 — T10 ADR-TR-5·ADR-T1-3
  // [이유] OFFICIAL mock 제거. limit=6(모바일 가로 스크롤 기준).
  const { data: collectionData } = useQuery({
    queryKey: queryKeys.collection.list({ kind: 'official', limit: 6 }),
    queryFn: () => collectionClient.list({ kind: 'official', limit: 6 }),
    staleTime: 1000 * 60 * 5,
    throwOnError: false,
  });
  const officialCollections = collectionData?.items ?? [];

  // fe-mobile-live-data: 폴리오 그리드 실데이터 — ExplorePage.tsx 데스크톱과 동일 useFolioFeed 재사용
  // M7 P1: q/tags/duration 서버 조회로 교체(클라이언트 제목/vibe 필터 제거)
  const {
    allFolios,
    isLoading: feedLoading,
    error: feedError,
    refetch: refetchFeed,
  } = useFolioFeed({
    sort: 'latest',
    q: exploreFilters.q || undefined,
    tags: exploreFilters.tags,
    minDurationDays: exploreFilters.minDurationDays,
    maxDurationDays: exploreFilters.maxDurationDays,
  });
  const filtered = allFolios;

  // [수정] 2026-09-18 QA(M-8): 빈 상태 문구·필터 초기화 버튼 분기용(ExplorePage.tsx 와 동일 조건)
  const hasActiveFilter = exploreFilters.tags.length > 0 || exploreFilters.duration !== '전체';

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: BG1 }}>
      {/* 헤더 */}
      <Box sx={{ bgcolor: c.paper[0][L], p: '14px 20px 0', borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: TEXT_HI, mb: '12px' }}>
          발견하기 ◎
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', mb: '12px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              bgcolor: BG1,
              borderRadius: '10px',
              p: '8px 12px',
              border: `1px solid ${BORDER}`,
              flex: 1,
            }}
          >
            {/* [이유] W4: 검색 아이콘 🔍 → ⌕ (시안 explore.jsx L54 정렬) */}
            <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: TEXT_LOW }}>
              ⌕
            </Box>
            <InputBase
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="장소, 무드, 동반자로 검색..."
              inputProps={{ 'aria-label': '발견 검색' }}
              sx={{ flex: 1, fontSize: 12, color: TEXT_HI }}
            />
          </Box>
          {/* M7 P1: 필터 모달(기간·테마) 오픈 버튼 — ExplorePage.tsx 데스크톱과 동일 컴포넌트 재사용 */}
          <ButtonBase
            onClick={() => setFilterModalOpen(true)}
            aria-label="필터 열기"
            aria-haspopup="dialog"
            aria-expanded={filterModalOpen}
            sx={{
              flexShrink: 0,
              bgcolor: BG1,
              border: `1px solid ${BORDER}`,
              borderRadius: '10px',
              px: '12px',
              color: TEXT_HI,
              fontSize: 15,
            }}
          >
            ⚙
          </ButtonBase>
        </Box>
        <Box sx={{ display: 'flex', gap: '6px', pb: '12px', overflowX: 'auto' }}>
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <ButtonBase
                key={f.id}
                onClick={() => setFilter(f.id)}
                aria-label={`필터 ${f.label}`}
                aria-pressed={active}
                sx={{
                  flexShrink: 0,
                  borderRadius: 20,
                  px: '12px',
                  py: '5px',
                  fontSize: 11,
                  border: `1px solid ${active ? BRAND : BORDER}`,
                  bgcolor: active ? `${BRAND}1A` : c.paper[0][L],
                  color: active ? BRAND_TEXT_STRONG : TEXT_MID,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {f.label}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
      {/* 공식 컬렉션 — 0건이면 graceful(섹션 숨김, R-4) */}
      {officialCollections.length > 0 && (
        <Box sx={{ pt: '18px' }}>
          <Box
            sx={{
              px: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: '10px',
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HI }}>
              🏛 공식 컬렉션
            </Typography>
            <ButtonBase
              sx={{ fontSize: 11, color: BRAND_TEXT_STRONG }}
              aria-label="공식 컬렉션 전체"
            >
              전체 →
            </ButtonBase>
          </Box>
          {/* [목적] 공식 컬렉션 실데이터 렌더 — T10 ADR-TR-5: 0건이면 영역 비움(graceful) */}
          <Box sx={{ display: 'flex', gap: '10px', px: '20px', overflowX: 'auto', pb: '4px' }}>
            {officialCollections.map((col: Collection, idx: number) => (
              <ButtonBase
                key={col.id}
                onClick={() => navigate(`/collections/${col.id}`)}
                aria-label={`공식 컬렉션 ${col.title}`}
                sx={{
                  minWidth: 160,
                  bgcolor: MOBILE_COLLECTION_BG_CYCLE[idx % MOBILE_COLLECTION_BG_CYCLE.length],
                  borderRadius: '14px',
                  p: '13px 14px',
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '8px' }}>
                  <Box component="span" sx={{ fontSize: 22 }}>
                    🏛
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9,
                      bgcolor: `${BRAND}22`,
                      color: BRAND,
                      borderRadius: '5px',
                      px: '5px',
                      py: '1px',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    공식
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: TEXT_HI,
                    lineHeight: 1.35,
                    mb: '4px',
                  }}
                >
                  {col.title}
                </Typography>
                <Typography sx={{ fontSize: 10, color: TEXT_MID }}>
                  폴리오 {col.itemCount}개
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )}{' '}
      {/* end 공식 컬렉션 조건부 렌더 */}
      {/* 폴리오 그리드 — 실데이터 */}
      <Box sx={{ p: '20px 20px 32px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '12px',
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HI }}>
            {filter === 'all' || !filter
              ? '인기 폴리오'
              : `${FILTERS.find((f) => f.id === filter)?.label} 폴리오`}
          </Typography>
          {!feedLoading && (
            <Typography sx={{ fontSize: 11, color: TEXT_MID }}>{filtered.length}개</Typography>
          )}
        </Box>

        {feedLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}>
            <CircularProgress size={22} aria-label="폴리오 불러오는 중" />
          </Box>
        )}

        {Boolean(feedError) && !feedLoading && (
          <InlineErrorState message="폴리오를 불러오지 못했습니다." onRetry={refetchFeed} />
        )}

        {/* [수정] 2026-09-18 QA(M-8): ExplorePage.tsx 와 동일 — 필터 0건과 진짜 빈 목록을 구분하고
            필터가 걸린 경우에만 초기화 버튼을 노출한다. */}
        {!feedLoading && !feedError && filtered.length === 0 && (
          <Box sx={{ textAlign: 'center', p: '32px 20px' }}>
            <Typography sx={{ fontSize: 13, color: TEXT_MID }}>
              {exploreFilters.q
                ? '검색 결과가 없습니다.'
                : hasActiveFilter
                  ? '조건에 맞는 폴리오가 없습니다.'
                  : '아직 폴리오가 없습니다.'}
            </Typography>
            {(exploreFilters.q || hasActiveFilter) && (
              <ButtonBase
                onClick={() => exploreFilters.reset()}
                sx={{
                  mt: '12px',
                  px: '16px',
                  py: '8px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: BRAND,
                  border: `1px solid ${BORDER}`,
                  borderRadius: '999px',
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                }}
              >
                필터 초기화
              </ButtonBase>
            )}
          </Box>
        )}

        {!feedLoading && filtered.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {filtered.map((f) => (
              <MobileExploreCard key={f.id} f={f} onClick={() => navigate(`/folio/${f.id}`)} />
            ))}
          </Box>
        )}
      </Box>
      {/* 필터 모달 — ExplorePage.tsx 데스크톱과 동일 공용 컴포넌트 */}
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        duration={exploreFilters.duration}
        onDurationChange={exploreFilters.setDuration}
        tags={exploreFilters.tags}
        onTagsChange={exploreFilters.setTags}
        onReset={exploreFilters.reset}
        resultCount={filtered.length}
      />
    </Box>
  );
}
